import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  ArrowLeft, Save, RefreshCw, AlertTriangle, CheckCircle2,
  Plus, Trash2, Send, ThumbsUp, ThumbsDown, X,
} from 'lucide-react';

import { cashReconciliationApi } from '@/api/cashReconciliation.api';
import { usePermission }          from '@/hooks/usePermission';
import { Permissions }            from '@/constants/permissions';
import { AppRoutes }              from '@/constants/routes';
import { Spinner }                from '@/components/ui/Spinner';
import { cn }                     from '@/utils/cn';
import type { RootState }         from '@/state/store';
import { GlAccountLookup, LocationLookup } from '@/components/lookups/EntityLookups';

import type {
  CashReconciliationDto,
  CashReconciliationLineDto,
  RecordDenominationLineRequest,
} from '@/types/cashReconciliation.types';
import {
  CASH_RECON_STATUS_COLORS,
  CASH_RECON_STATUS_LABELS,
} from '@/types/cashReconciliation.types';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtAmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2 });
const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

// ── Denomination line row (edit-in-table) ─────────────────────────────────────
interface LineRow {
  cashDenominationId?: string;
  denominationValue:   number;
  denominationType:    'Note' | 'Coin';
  count:               number;
  currencyCode:        string;
  sortOrder:           number;
}

function lineAmount(row: LineRow) {
  return row.denominationValue * row.count;
}

// ── Page ──────────────────────────────────────────────────────────────────────
export const CashReconciliationWorkspacePage = () => {
  const navigate          = useNavigate();
  const { id }            = useParams<{ id: string }>();
  const isNew             = !id || id === 'new';
  const { hasPermission } = usePermission();
  useSelector((s: RootState) => s.session.session?.companyId ?? '');

  const canEdit    = hasPermission((Permissions as any).CASH_RECON_EDIT);
  const canSubmit  = hasPermission((Permissions as any).CASH_RECON_SUBMIT);
  const canApprove = hasPermission((Permissions as any).CASH_RECON_APPROVE);
  const canDelete  = hasPermission((Permissions as any).CASH_RECON_DELETE);

  // ── State ──────────────────────────────────────────────────────────────────
  const [recon,   setRecon]   = useState<CashReconciliationDto | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving,  setSaving]  = useState(false);
  const [toast,   setToast]   = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // New form fields
  const [form, setForm] = useState({
    reconciliationDate: new Date().toISOString().split('T')[0],
    currencyCode:       'LKR',
    exchangeRate:       '1',
    shiftCode:          '',
    locationId:         '',
    cashAccountId:      '',
    openingFloat:       '',
    remarks:            '',
  });

  // Cash amounts fields (shown after creation)
  const [cashAmounts, setCashAmounts] = useState({
    cashSalesAmount:    '',
    cashReceiptsAmount: '',
    cashPayoutsAmount:  '',
    pettyCashAmount:    '',
  });
  const [savingAmounts, setSavingAmounts] = useState(false);

  // Denomination lines
  const [lines,      setLines]      = useState<LineRow[]>([]);
  const [savingCount, setSavingCount] = useState(false);
  const [varianceReason, setVarianceReason] = useState('');

  // Action modals
  const [showRejectModal,          setShowRejectModal]          = useState(false);
  const [showApproveVarianceModal, setShowApproveVarianceModal] = useState(false);
  const [showRejectVarianceModal,  setShowRejectVarianceModal]  = useState(false);
  const [showDeleteModal,          setShowDeleteModal]          = useState(false);
  const [rejectReason,             setRejectReason]             = useState('');
  const [varianceRemarks,          setVarianceRemarks]          = useState('');
  const [actionBusy,               setActionBusy]               = useState(false);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  // ── Load existing reconciliation ───────────────────────────────────────────
  const loadRecon = useCallback(async () => {
    if (isNew || !id) return;
    setLoading(true);
    try {
      const res = await cashReconciliationApi.getById(id);
      const rec = res.data?.data ?? null;
      setRecon(rec);
      if (rec) {
        setCashAmounts({
          cashSalesAmount:    rec.cashSalesAmount.toString(),
          cashReceiptsAmount: rec.cashReceiptsAmount.toString(),
          cashPayoutsAmount:  rec.cashPayoutsAmount.toString(),
          pettyCashAmount:    rec.pettyCashAmount.toString(),
        });
        setVarianceReason(rec.varianceReason ?? '');
        setLines(rec.denominationLines.map((l: CashReconciliationLineDto) => ({
          cashDenominationId: l.cashDenominationId ?? undefined,
          denominationValue:  l.denominationValue,
          denominationType:   l.denominationType,
          count:              l.count,
          currencyCode:       l.currencyCode,
          sortOrder:          l.sortOrder,
        })));
      }
    } catch (err) {
      console.error('Failed to load cash reconciliation', err);
      setToast({ message: 'Failed to load reconciliation', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { loadRecon(); }, [loadRecon]);

  // ── Create ────────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!form.reconciliationDate || !form.currencyCode) {
      setToast({ message: 'Date and currency are required.', type: 'error' });
      return;
    }
    setSaving(true);
    try {
      const res = await cashReconciliationApi.create({
        reconciliationDate: form.reconciliationDate,
        currencyCode:       form.currencyCode,
        exchangeRate:       parseFloat(form.exchangeRate) || 1,
        shiftCode:          form.shiftCode   || undefined,
        locationId:         form.locationId  || undefined,
        cashAccountId:      form.cashAccountId || undefined,
        openingFloat:       parseFloat(form.openingFloat) || 0,
        remarks:            form.remarks     || undefined,
      });
      const newRec = res.data?.data;
      setToast({ message: 'Reconciliation created', type: 'success' });
      if (newRec?.id) {
        navigate((AppRoutes.CASH_RECON_DETAIL as string).replace(':id', newRec.id));
      }
    } catch (err: any) {
      setToast({ message: err?.response?.data?.message ?? 'Failed to create reconciliation', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ── Save cash amounts ─────────────────────────────────────────────────────
  const handleSaveCashAmounts = async () => {
    if (!recon) return;
    setSavingAmounts(true);
    try {
      const res = await cashReconciliationApi.recordCashAmounts(recon.id, {
        cashSalesAmount:    parseFloat(cashAmounts.cashSalesAmount)    || 0,
        cashReceiptsAmount: parseFloat(cashAmounts.cashReceiptsAmount) || 0,
        cashPayoutsAmount:  parseFloat(cashAmounts.cashPayoutsAmount)  || 0,
        pettyCashAmount:    parseFloat(cashAmounts.pettyCashAmount)    || 0,
      });
      setRecon(res.data?.data ?? recon);
      setToast({ message: 'Cash amounts saved', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.message ?? 'Failed to save cash amounts', type: 'error' });
    } finally {
      setSavingAmounts(false);
    }
  };

  // ── Save denomination count ───────────────────────────────────────────────
  const handleSaveCount = async () => {
    if (!recon) return;
    setSavingCount(true);
    try {
      const denomLines: RecordDenominationLineRequest[] = lines.map((l, i) => ({
        cashDenominationId: l.cashDenominationId,
        denominationValue:  l.denominationValue,
        denominationType:   l.denominationType,
        count:              l.count,
        currencyCode:       l.currencyCode,
        sortOrder:          i,
      }));
      const res = await cashReconciliationApi.recordActualCount(recon.id, {
        actualCountedAmount: lines.reduce((s, l) => s + lineAmount(l), 0),
        varianceReason:      varianceReason || undefined,
        denominationLines:   denomLines,
      });
      setRecon(res.data?.data ?? recon);
      setToast({ message: 'Denomination count saved', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.message ?? 'Failed to save denomination count', type: 'error' });
    } finally {
      setSavingCount(false);
    }
  };

  // ── Workflow actions ──────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!recon) return;
    setActionBusy(true);
    try {
      const res = await cashReconciliationApi.submit(recon.id);
      setRecon(res.data?.data ?? recon);
      setToast({ message: 'Reconciliation submitted', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.message ?? 'Failed to submit', type: 'error' });
    } finally {
      setActionBusy(false);
    }
  };

  const handleApprove = async () => {
    if (!recon) return;
    setActionBusy(true);
    try {
      const res = await cashReconciliationApi.approve(recon.id);
      setRecon(res.data?.data ?? recon);
      setToast({ message: 'Reconciliation approved', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.message ?? 'Failed to approve', type: 'error' });
    } finally {
      setActionBusy(false);
    }
  };

  const handleReject = async () => {
    if (!recon || !rejectReason.trim()) {
      setToast({ message: 'A reason is required to reject.', type: 'error' });
      return;
    }
    setActionBusy(true);
    try {
      const res = await cashReconciliationApi.reject(recon.id, { reason: rejectReason });
      setRecon(res.data?.data ?? recon);
      setToast({ message: 'Reconciliation rejected', type: 'success' });
      setShowRejectModal(false);
      setRejectReason('');
    } catch (err: any) {
      setToast({ message: err?.response?.data?.message ?? 'Failed to reject', type: 'error' });
    } finally {
      setActionBusy(false);
    }
  };

  const handleApproveVariance = async () => {
    if (!recon) return;
    setActionBusy(true);
    try {
      const res = await cashReconciliationApi.approveVariance(recon.id, { remarks: varianceRemarks || undefined });
      setRecon(res.data?.data ?? recon);
      setToast({ message: 'Variance approved', type: 'success' });
      setShowApproveVarianceModal(false);
    } catch (err: any) {
      setToast({ message: err?.response?.data?.message ?? 'Failed to approve variance', type: 'error' });
    } finally {
      setActionBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!recon) return;
    setActionBusy(true);
    try {
      await cashReconciliationApi.deleteRecon(recon.id);
      setToast({ message: 'Reconciliation deleted', type: 'success' });
      setTimeout(() => navigate(AppRoutes.CASH_RECON as string), 800);
    } catch (err: any) {
      setToast({ message: err?.response?.data?.message ?? 'Failed to delete', type: 'error' });
      setShowDeleteModal(false);
    } finally {
      setActionBusy(false);
    }
  };

  // ── Denomination lines helpers ─────────────────────────────────────────────
  const addLine = () => {
    setLines(prev => [
      ...prev,
      {
        denominationValue: 0,
        denominationType:  'Note',
        count:             0,
        currencyCode:      recon?.currencyCode ?? 'LKR',
        sortOrder:         prev.length,
      },
    ]);
  };

  const removeLine = (idx: number) => setLines(prev => prev.filter((_, i) => i !== idx));

  const updateLine = (idx: number, patch: Partial<LineRow>) =>
    setLines(prev => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  // ── Computed values ────────────────────────────────────────────────────────
  const localExpected =
    (parseFloat(form.openingFloat) || 0)
    + (parseFloat(cashAmounts.cashSalesAmount)    || 0)
    + (parseFloat(cashAmounts.cashReceiptsAmount) || 0)
    - (parseFloat(cashAmounts.cashPayoutsAmount)  || 0)
    - (parseFloat(cashAmounts.pettyCashAmount)    || 0);

  const actualCounted = lines.reduce((s, l) => s + lineAmount(l), 0);
  void ((recon ? recon.expectedClosingBalance : localExpected) - actualCounted); // variance computed on display

  const isEditable  = recon?.status === 'Draft';
  const isSubmitted = recon?.status === 'Submitted';

  // ─── New reconciliation form ───────────────────────────────────────────────
  if (isNew) {
    return (
      <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
        <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(AppRoutes.CASH_RECON as string)}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-slate-900 dark:text-white">New Cash Reconciliation</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                Provide date, currency and opening float to start.
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 space-y-5">

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Reconciliation Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date" value={form.reconciliationDate}
                    onChange={e => setForm(f => ({ ...f, reconciliationDate: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                               bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                               focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Currency <span className="text-red-500">*</span>
                  </label>
                  <input
                    value={form.currencyCode}
                    onChange={e => setForm(f => ({ ...f, currencyCode: e.target.value.toUpperCase() }))}
                    placeholder="e.g. LKR"
                    maxLength={3}
                    className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                               bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                               focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Exchange Rate
                  </label>
                  <input
                    type="number" step="0.0001" min="0" value={form.exchangeRate}
                    onChange={e => setForm(f => ({ ...f, exchangeRate: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                               bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                               focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-right"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Opening Float <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number" step="0.01" min="0" value={form.openingFloat}
                    onChange={e => setForm(f => ({ ...f, openingFloat: e.target.value }))}
                    placeholder="0.00"
                    className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                               bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                               focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-right"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Shift Code</label>
                  <input
                    value={form.shiftCode}
                    onChange={e => setForm(f => ({ ...f, shiftCode: e.target.value }))}
                    placeholder="Optional"
                    className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                               bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                               focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                  />
                </div>
                <LocationLookup
                  label="Location"
                  value={form.locationId}
                  onChange={v => setForm(f => ({ ...f, locationId: v ?? '' }))}
                />
                <GlAccountLookup
                  label="Cash Account"
                  value={form.cashAccountId}
                  onChange={v => setForm(f => ({ ...f, cashAccountId: v ?? '' }))}
                  hint="The till being counted."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Remarks</label>
                <textarea
                  value={form.remarks}
                  onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))}
                  rows={3}
                  placeholder="Optional remarks…"
                  className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                             bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                             focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => navigate(AppRoutes.CASH_RECON as string)}
                className="px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate} disabled={saving}
                className="flex items-center gap-2 px-5 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60"
              >
                {saving ? <Spinner size="sm" /> : <Save size={15} />}
                Create Reconciliation
              </button>
            </div>
          </div>
        </div>

        {toast && (
          <div className={cn(
            'fixed bottom-5 right-5 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium z-50',
            toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white',
          )}>
            {toast.type === 'success' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
            {toast.message}
          </div>
        )}
      </div>
    );
  }

  // ─── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!recon) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400">
        <p>Reconciliation not found.</p>
      </div>
    );
  }

  // ─── Existing reconciliation workspace ────────────────────────────────────
  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* ── Header bar ───────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(AppRoutes.CASH_RECON as string)}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-slate-900 dark:text-white">
                {recon.reconciliationNumber}
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                {fmtDate(recon.reconciliationDate)}
                {recon.shiftCode ? ` · Shift: ${recon.shiftCode}` : ''}
                {` · ${recon.currencyCode}`}
              </p>
            </div>
            <span className={cn(
              'px-2.5 py-1 rounded-full text-xs font-semibold',
              CASH_RECON_STATUS_COLORS[recon.status] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400',
            )}>
              {CASH_RECON_STATUS_LABELS[recon.status] ?? recon.status}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={loadRecon}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>

            {isEditable && canDelete && (
              <button
                onClick={() => setShowDeleteModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-red-600 dark:text-red-400"
              >
                <Trash2 size={14} />
                Delete
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto p-6 space-y-6">

        {/* ── Section 1: Cash Amounts ─────────────────────────────────────── */}
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-4">
            Cash Amounts
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {([
              { key: 'cashSalesAmount',    label: 'Cash Sales' },
              { key: 'cashReceiptsAmount', label: 'Cash Receipts' },
              { key: 'cashPayoutsAmount',  label: 'Cash Payouts' },
              { key: 'pettyCashAmount',    label: 'Petty Cash' },
            ] as const).map(({ key, label }) => (
              <div key={key}>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">{label}</label>
                <input
                  type="number" step="0.01" min="0"
                  value={cashAmounts[key]}
                  onChange={e => setCashAmounts(a => ({ ...a, [key]: e.target.value }))}
                  disabled={!isEditable || !canEdit}
                  placeholder="0.00"
                  className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                             bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                             focus:ring-2 focus:ring-teal-500 outline-none text-right
                             disabled:bg-slate-50 dark:disabled:bg-slate-800 disabled:text-slate-400"
                />
              </div>
            ))}
          </div>

          {/* Expected Closing Balance computed card */}
          <div className="mt-4 flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl border border-slate-200 dark:border-slate-600">
            <div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Opening Float</p>
              <p className="text-sm font-mono text-slate-700 dark:text-slate-300 mt-0.5">{fmtAmt(recon.openingFloat)}</p>
            </div>
            <div className="text-slate-400 text-lg">→</div>
            <div className="text-right">
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Expected Closing Balance</p>
              <p className="text-xl font-bold font-mono text-teal-700 dark:text-teal-400 mt-0.5">
                {fmtAmt(recon.expectedClosingBalance || localExpected)}
              </p>
            </div>
          </div>

          {isEditable && canEdit && (
            <div className="flex justify-end mt-3">
              <button
                onClick={handleSaveCashAmounts} disabled={savingAmounts}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60"
              >
                {savingAmounts ? <Spinner size="sm" /> : <Save size={14} />}
                Save Amounts
              </button>
            </div>
          )}
        </div>

        {/* ── Section 2: Denomination Count ──────────────────────────────── */}
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
              Denomination Count
            </h2>
            {isEditable && canEdit && (
              <button
                onClick={addLine}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-teal-200 dark:border-teal-800 text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors"
              >
                <Plus size={12} />
                Add Denomination
              </button>
            )}
          </div>

          {lines.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">
              No denomination lines. Add one to record physical cash count.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 dark:border-slate-700">
                    <th className="text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider pb-2">Type</th>
                    <th className="text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider pb-2">Value</th>
                    <th className="text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider pb-2">Count</th>
                    <th className="text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider pb-2">Amount</th>
                    {isEditable && canEdit && <th className="w-8"></th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 dark:divide-slate-700/50">
                  {lines.map((line, idx) => (
                    <tr key={idx}>
                      <td className="py-2 pr-3">
                        <select
                          value={line.denominationType}
                          onChange={e => updateLine(idx, { denominationType: e.target.value as 'Note' | 'Coin' })}
                          disabled={!isEditable || !canEdit}
                          className="text-xs border border-slate-200 dark:border-slate-600 rounded px-2 py-1
                                     bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                                     focus:ring-1 focus:ring-teal-500 outline-none disabled:opacity-60"
                        >
                          <option value="Note">Note</option>
                          <option value="Coin">Coin</option>
                        </select>
                      </td>
                      <td className="py-2 px-2 text-right">
                        <input
                          type="number" step="0.01" min="0"
                          value={line.denominationValue}
                          onChange={e => updateLine(idx, { denominationValue: parseFloat(e.target.value) || 0 })}
                          disabled={!isEditable || !canEdit}
                          className="w-24 text-right text-xs border border-slate-200 dark:border-slate-600 rounded px-2 py-1
                                     bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                                     focus:ring-1 focus:ring-teal-500 outline-none disabled:opacity-60"
                        />
                      </td>
                      <td className="py-2 px-2 text-right">
                        <input
                          type="number" step="1" min="0"
                          value={line.count}
                          onChange={e => updateLine(idx, { count: parseInt(e.target.value) || 0 })}
                          disabled={!isEditable || !canEdit}
                          className="w-20 text-right text-xs border border-slate-200 dark:border-slate-600 rounded px-2 py-1
                                     bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                                     focus:ring-1 focus:ring-teal-500 outline-none disabled:opacity-60"
                        />
                      </td>
                      <td className="py-2 pl-2 text-right font-mono text-xs text-slate-700 dark:text-slate-300">
                        {fmtAmt(lineAmount(line))}
                      </td>
                      {isEditable && canEdit && (
                        <td className="py-2 pl-2">
                          <button
                            onClick={() => removeLine(idx)}
                            className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-400 hover:text-red-600 transition-colors"
                          >
                            <X size={12} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-slate-200 dark:border-slate-700">
                    <td colSpan={3} className="pt-2 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">
                      Actual Counted Total
                    </td>
                    <td className="pt-2 text-right font-bold font-mono text-slate-900 dark:text-white">
                      {fmtAmt(actualCounted)}
                    </td>
                    {isEditable && canEdit && <td />}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {isEditable && canEdit && lines.length > 0 && (
            <div className="mt-3">
              <div className="mb-2">
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                  Variance Reason (if applicable)
                </label>
                <input
                  value={varianceReason}
                  onChange={e => setVarianceReason(e.target.value)}
                  placeholder="Explain variance…"
                  className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                             bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                             focus:ring-2 focus:ring-teal-500 outline-none"
                />
              </div>
              <div className="flex justify-end">
                <button
                  onClick={handleSaveCount} disabled={savingCount}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60"
                >
                  {savingCount ? <Spinner size="sm" /> : <Save size={14} />}
                  Save Count
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Section 3: Variance display ─────────────────────────────────── */}
        {recon.actualCountedAmount > 0 && (
          <div className={cn(
            'flex items-center justify-between p-4 rounded-xl border font-semibold',
            Math.abs(recon.variance) < 0.005
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
              : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400',
          )}>
            <span className="text-sm">
              Variance = Expected ({fmtAmt(recon.expectedClosingBalance)}) − Actual ({fmtAmt(recon.actualCountedAmount)})
            </span>
            <span className="font-mono text-base">
              {Math.abs(recon.variance) < 0.005 ? '✓ 0.00' : fmtAmt(recon.variance)}
            </span>
          </div>
        )}

        {/* ── Variance approval banner ─────────────────────────────────────── */}
        {recon.requiresVarianceApproval && recon.varianceApprovalStatus !== 'Approved' && (
          <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
            <AlertTriangle size={18} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">Variance Approval Required</p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                This reconciliation has a variance that requires approval before it can be posted.
                {recon.varianceApprovalStatus && ` Current status: ${recon.varianceApprovalStatus}`}
              </p>
            </div>
            {canApprove && recon.varianceApprovalStatus !== 'Pending' && (
              <button
                onClick={() => setShowApproveVarianceModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-lg transition-colors"
              >
                <ThumbsUp size={12} />
                Approve Variance
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Bottom action bar ─────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 px-6 py-3 flex items-center justify-end gap-3">
        {isEditable && canSubmit && (
          <button
            onClick={handleSubmit} disabled={actionBusy}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60"
          >
            {actionBusy ? <Spinner size="sm" /> : <Send size={15} />}
            Submit
          </button>
        )}
        {isSubmitted && canApprove && (
          <>
            <button
              onClick={handleApprove} disabled={actionBusy}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60"
            >
              {actionBusy ? <Spinner size="sm" /> : <ThumbsUp size={15} />}
              Approve
            </button>
            <button
              onClick={() => setShowRejectModal(true)} disabled={actionBusy}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60"
            >
              <ThumbsDown size={15} />
              Reject
            </button>
          </>
        )}
      </div>

      {/* ── Toast ────────────────────────────────────────────────────────── */}
      {toast && (
        <div className={cn(
          'fixed bottom-5 right-5 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium z-50',
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white',
        )}>
          {toast.type === 'success' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
          {toast.message}
        </div>
      )}

      {/* ── Reject modal ─────────────────────────────────────────────────── */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Reject Reconciliation</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Provide a reason for rejection.</p>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              rows={3}
              placeholder="Reason for rejection…"
              className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                         bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                         focus:ring-2 focus:ring-teal-500 outline-none resize-none"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 text-sm text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject} disabled={actionBusy || !rejectReason.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60"
              >
                {actionBusy ? <Spinner size="sm" /> : <ThumbsDown size={14} />}
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Approve Variance modal ────────────────────────────────────────── */}
      {showApproveVarianceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Approve Variance</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              Variance: <span className="font-mono font-semibold text-red-600 dark:text-red-400">{fmtAmt(recon.variance)}</span>
            </p>
            <textarea
              value={varianceRemarks}
              onChange={e => setVarianceRemarks(e.target.value)}
              rows={3}
              placeholder="Optional remarks…"
              className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                         bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                         focus:ring-2 focus:ring-teal-500 outline-none resize-none"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowApproveVarianceModal(false)}
                className="px-4 py-2 text-sm text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleApproveVariance} disabled={actionBusy}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60"
              >
                {actionBusy ? <Spinner size="sm" /> : <ThumbsUp size={14} />}
                Approve Variance
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject Variance modal ─────────────────────────────────────────── */}
      {showRejectVarianceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Reject Variance</h3>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              rows={3}
              placeholder="Reason for rejecting variance…"
              className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                         bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                         focus:ring-2 focus:ring-teal-500 outline-none resize-none"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowRejectVarianceModal(false)}
                className="px-4 py-2 text-sm text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                disabled={actionBusy || !rejectReason.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60"
              >
                {actionBusy ? <Spinner size="sm" /> : <ThumbsDown size={14} />}
                Reject Variance
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete confirmation ───────────────────────────────────────────── */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 w-full max-w-sm mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full">
                <Trash2 size={18} className="text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900 dark:text-white">Delete Reconciliation</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">This action cannot be undone.</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-5">
              Delete reconciliation{' '}
              <span className="font-semibold font-mono">{recon.reconciliationNumber}</span>?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-sm text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete} disabled={actionBusy}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60"
              >
                {actionBusy ? <Spinner size="sm" /> : <Trash2 size={14} />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
